import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { SectionHeading } from "@/components/base/Ui";
import { BlacklistRow } from "@/components/base/BlacklistRow";
import { blacklist } from "@/mocks/blacklist";
import { site } from "@/mocks/site";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import HeroCarousel from "./components/HeroCarousel";
import CategoryQuickEntry from "./components/CategoryQuickEntry";
import LatestNews from "./components/LatestNews";
import { events } from "@/mocks/events";
import type { EventItem, BlacklistEntry } from "@/types";

export default function Home() {
  useDocumentMeta({
    title: "51-每日大赛-每日赛事资讯及今日黑榜排行",
    description:
      "51-每日大赛为您提供每日最新赛事资讯、比赛安排及今日黑榜mdrs排行，覆盖多类赛事动态，助您掌握赛事实时信息。",
    keywords: "51-每日大赛,每日赛事,赛事资讯,今日黑榜mdrs,比赛排行,赛事动态,赛事更新",
  });

  const focusIds = ["evt-001", "evt-007", "evt-003", "evt-005"];
  const focus = focusIds
    .map((id) => events.find((e) => e.id === id))
    .filter(Boolean) as EventItem[];
  const topList = (blacklist as BlacklistEntry[]).slice(0, 5);

  return (
    <Layout>
      <HeroCarousel slides={focus} />

      <div className="mx-auto w-full px-4 md:px-6">
        <section className="-mt-6 relative z-10 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {site.stats.map((s) => (
              <div key={s.label} className="flex flex-col items-center rounded-md bg-background-100 px-3 py-4 text-center">
                <span className="text-xl font-extrabold text-primary-700 md:text-2xl">{s.value}</span>
                <span className="mt-1 text-xs text-foreground-500">{s.label}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <SectionHeading
            title="赛事分类快捷入口"
            subtitle="按赛事类别快速进入，查看对应分类的资讯、赛程与安排"
            icon="ri-apps-2-line"
          />
          <CategoryQuickEntry />
        </section>

        <section className="mt-12 rounded-lg border border-accent-200 bg-accent-50/60 p-4 md:p-6">
          <SectionHeading
            title="今日黑榜 mdrs 排行 · 前 5 预览"
            subtitle="实时展示当日表现最差的队伍与选手，mdrs 失分指数越高表现越差"
            icon="ri-fire-line"
            to="/blacklist"
            toLabel="查看完整黑榜排行"
          />
          <div className="flex flex-col gap-3">
            {topList.map((entry) => (
              <BlacklistRow key={entry.rank} entry={entry} showMetrics={false} />
            ))}
          </div>
        </section>

        <section className="mt-12">
          <SectionHeading
            title="最新赛事资讯"
            subtitle="按时间倒序排列的每日赛事资讯与赛程安排"
            icon="ri-newspaper-line"
            to="/category/football"
            toLabel="更多分类资讯"
          />
          <LatestNews />
        </section>

        <section className="mt-12 mb-4 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center">
            <div>
              <span className="flex w-fit items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700">
                <i className="ri-information-line"></i>
                关于本站
              </span>
              <h2 className="mt-4 text-xl font-extrabold text-foreground-950 md:text-2xl">
                为什么选择 {site.brand} 掌握赛事动态
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">{site.intro}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                我们以每日更新为核心节奏，为体育赛事关注者、分析师与竞猜用户提供结构清晰的资讯分区、
                权威的黑榜排行与可追溯的赛事数据，帮助你在开赛前快速掌握形势。
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link
                  to="/about"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-team-line"></i>
                  了解我们
                </Link>
                <Link
                  to="/blacklist"
                  className="flex items-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                >
                  <i className="ri-bar-chart-box-line"></i>
                  查看黑榜排行
                </Link>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { icon: "ri-flashlight-line", t: "每日更新", d: "资讯与排行每日 08:00 准时更新" },
                { icon: "ri-shield-star-line", t: "权威排行", d: "多指标加权计算 mdrs 失分指数" },
                { icon: "ri-layout-masonry-line", t: "分类清晰", d: "六大类别、多维筛选快速定位" },
                { icon: "ri-device-line", t: "多端适配", d: "桌面与手机端一致的浏览体验" },
              ].map((f) => (
                <div key={f.t} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-xl text-accent-700">
                    <i className={f.icon}></i>
                  </span>
                  <p className="mt-3 text-sm font-bold text-foreground-900">{f.t}</p>
                  <p className="mt-1 text-xs leading-relaxed text-foreground-500">{f.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
}